import { useState, useCallback } from "react";
import { Stack } from "@mui/material";
import { DashboardLayout } from "./layout/DashboardLayout";
import { QuickStartSection } from "./sections/QuickStartSection";
import { ProjectsSection } from "./sections/ProjectsSection";
import { ErrorState } from "./components/ErrorState";
import { DashboardModalsHost } from "./modals/DashboardModalsHost";
import { useDashboardQueryState } from "./hooks/useDashboardQueryState";
import { useDashboardProjects } from "./hooks/useDashboardProjects";
import { filterProjects } from "./helpers/filter-projects.helper";
import { ModalState } from "./modals/modal-types";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../redux/hooks/redux";
import { useDraftsMutations } from "../drafts/hooks/useCreateDrafts";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";

// Types

export interface DashboardPageProps {
  onOpenProject?: (projectId: string) => void;
  onNavigate?: (path: string) => void;
  userProfile?: {
    name: string;
    email?: string;
    plan?: string;
  };
}

export const PageBuilderDashboard = ({
  userProfile = {
    name: "Alex Rivera",
    email: "alex@example.com",
    plan: "Pro Plan",
  },
}: DashboardPageProps) => {
  // Query state management
  const navigate = useNavigate();
  const queryState = useDashboardQueryState();
  const { rename, remove } = useDraftsMutations();
  const { showSnackbar } = useGlobalSnackbar();
  const isSubmitting = rename.isPending || remove.isPending;

  const { company } = useAppSelector((state) => state.userState);

  // Data fetching
  const { projects, isLoading, error, refetch } = useDashboardProjects();

  // Modal state
  const [modalState, setModalState] = useState<ModalState>({
    type: null,
    isOpen: false,
  });

  // Filter and sort projects
  const filteredProjects = filterProjects(projects, queryState.state.searchQuery, queryState.state.filterStatus, queryState.state.sortBy);

  const isEmpty = !isLoading && filteredProjects.length === 0 && projects.length === 0;

  // Modal handlers
  const openRenameModal = useCallback((projectId: string, projectName: string) => {
    setModalState({
      type: "rename",
      projectId,
      projectName,
      isOpen: true,
    });
  }, []);

  const openDeleteModal = useCallback((projectId: string, projectName: string) => {
    setModalState({
      type: "delete",
      projectId,
      projectName,
      isOpen: true,
    });
  }, []);

  const openDuplicateModal = useCallback((projectId: string) => {
    setModalState({
      type: "duplicate",
      projectId,
      isOpen: true,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      type: null,
      isOpen: false,
    });
  }, []);

  const handleModalConfirm = useCallback(
    // Crear un nuevo draft desde la pantalla del modal tempaltes
    (data?: any) => {
      if (modalState.type === "chooseTemplate") {
        const template = data?.template;
        if (!template?.id) return;

        const projectName = `${template.name ?? "New Project"}`;

        closeModal();

        navigate("/business/page-builder/loading", {
          state: {
            mode: "create",
            templateId: template.id,
            projectName,
            content: {
              html: template.html ?? "",
              css: template.css ?? "",
            },
            companyId: company.id,
          },
        });
      }

      if (modalState.type === "rename") {
        rename.mutate(
          { id: modalState.projectId!, projectName: data.name },
          {
            onSuccess: () => {
              closeModal();
              showSnackbar("Project renamed successfully", "success");
            },
            onError: () => {
              showSnackbar("Error renaming project", "error");
            },
          },
        );
      }

      if (modalState.type === "delete") {
        remove.mutate(
          { id: modalState.projectId! },
          {
            onSuccess: () => {
              closeModal();
              showSnackbar("Project deleted successfully", "success");
            },
            onError: () => {
              showSnackbar("Error deleting project", "error");
            },
          },
        );
      }
    },
    [modalState.type, closeModal, navigate, company?.id, rename, remove],
  );
  // Project action handlers
  const handleOpenProject = useCallback(
    (projectId: string, projectType: "draft" | "publish") => {
      navigate("/business/page-builder/loading", {
        state: {
          mode: "open",
          draftId: projectId,
          projectType,
        },
      });
    },
    [navigate],
  );

  const handleBlankSite = useCallback(() => {
    console.log("Blank site clicked");
  }, []);

  const handleChooseTemplate = useCallback(() => {
    setModalState({
      type: "chooseTemplate",
      isOpen: true,
    });
  }, []);

  const handleDuplicatePublished = useCallback(() => {
    // TODO: Show published projects to duplicate from
    console.log("Duplicate from published clicked");
  }, []);

  return (
    <>
      <DashboardLayout
        userProfile={userProfile}
        searchQuery={queryState.state.searchQuery}
        onSearchChange={queryState.updateSearch}
        filterStatus={queryState.state.filterStatus}
        onFilterChange={queryState.updateFilter}
        sortBy={queryState.state.sortBy}
        onSortChange={queryState.updateSort}
        viewMode={queryState.state.viewMode}
        onViewModeChange={queryState.updateViewMode}
      >
        <Stack spacing={4}>
          {/* Error State */}
          {error && !isLoading && <ErrorState title="Failed to load projects" message={error} onRetry={refetch} />}

          {/* Quick Start Section - only show on home view with no search/filter */}
          {queryState.state.searchQuery === "" && queryState.state.filterStatus === "all" && !error && (
            <QuickStartSection onBlankSite={handleBlankSite} onChooseTemplate={handleChooseTemplate} />
          )}

          {/* Projects Section */}
          <ProjectsSection
            projects={filteredProjects}
            viewMode={queryState.state.viewMode}
            isLoading={isLoading}
            isEmpty={isEmpty}
            onConfirm={handleChooseTemplate}
            onOpen={handleOpenProject}
            onRename={openRenameModal}
            onDuplicate={openDuplicateModal}
            onDelete={openDeleteModal}
          />
        </Stack>
      </DashboardLayout>

      {/* Modals Host */}
      <DashboardModalsHost
        modalState={modalState}
        onClose={() => {
          if (isSubmitting) return;
          closeModal();
        }}
        isSubmiting={isSubmitting}
        onConfirm={handleModalConfirm}
      />
    </>
  );
};
